/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBrandingColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining columns for the branding tenant list.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { Tenant } from '../api/brandingApi';

export function useBrandingColumns() {
  const { t } = useI18n();

  return useMemo(
    () => [
      { key: 'name', label: t('tenants.name'), visible: true, sortable: true },
      { key: 'logo', label: t('branding.branding'), visible: true, sortable: false },
      { key: 'isActive', label: t('branding.status'), visible: true, sortable: true },
      { key: 'createdAt', label: t('branding.created'), visible: true, sortable: true },
      { key: 'actions', label: t('branding.actions'), visible: true, sortable: false },
    ],
    [t]
  );
}

export function useBrandingColumnRenderers() {
  const { t } = useI18n();

  return useMemo(
    () => ({
      name: (value: string, item: Tenant) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground">{item.slug}</div>
        </div>
      ),
      logo: (value: unknown, item: Tenant) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {item.logo ? (
              <img
                src={item.logo}
                alt="Logo"
                className="h-6 w-6 rounded object-cover"
              />
            ) : (
              <div className="flex h-6 w-6 items-center justify-center rounded bg-muted">
                <Building2 className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
            {item.primaryColor && (
              <div
                className="h-4 w-4 rounded border border-border"
                style={{ backgroundColor: item.primaryColor }}
              />
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {item.primaryColor && item.secondaryColor ? 'Custom' : 'Default'}
          </div>
        </div>
      ),
      isActive: (value: boolean) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${
            value
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {value ? t('branding.active') : t('branding.inactive')}
        </span>
      ),
      createdAt: (value: string) => (
        <div className="text-sm text-muted-foreground">
          {new Date(value).toLocaleDateString()}
        </div>
      ),
    }),
    [t]
  );
}

